import { Dispatch, SetStateAction } from 'react';
import { CheckCircle, Cancel } from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import { Box, Button, Stack, Typography, useTheme } from '@mui/material';
import { FormikValues, useFormikContext } from 'formik';
import { Step } from '..';
import StepsFlowInput from './StepsFlowInput';
import {
  accessPropertyByString,
  important,
} from '../../../../utils/global/helper';

interface Props {
  steps: Step[];
  step: Step;
  stepIndex: number;
  currentStep: number;
  setCurrentStep: Dispatch<SetStateAction<number>>;
  isSequential: boolean;
  isFirstStepRequired?: boolean;
  isFirstStepComplete?: boolean;
  isVisited: boolean;
  submitText?: string;
}

const ICON_HEIGHT = '40px';

const checkIfStepHasValue = (
  step: Pick<Step, 'inputs'>,
  values: FormikValues,
) =>
  !!step?.inputs?.length &&
  step?.inputs?.every(input => {
    if (input.isOptional) return true;
    const value = accessPropertyByString(values, input.name as string);
    return !!value;
  });

function StepsFlowStep({
  step: stepProp,
  steps,
  stepIndex,
  currentStep,
  setCurrentStep,
  isSequential,
  isFirstStepRequired,
  isFirstStepComplete: isFirstStepCompleteProp,
  submitText,
  isVisited,
}: Props) {
  const theme = useTheme();
  const formik = useFormikContext();

  const {
    title,
    description,
    visitedDescription,
    isSubmit,
    inputsGap = 2.5,
    selectButtonText = 'Edit',
    buttonText,
    onNavigate,
    isError,
    banner,
    ...step
  } = stepProp;

  const isActive = stepIndex === currentStep;
  const hasValue = checkIfStepHasValue(step, formik.values as FormikValues);
  const isDisabled =
    !hasValue ||
    (isSubmit &&
      (!formik.isValid || formik.isValidating || formik.isSubmitting));
  const isComplete = isSequential
    ? stepIndex < currentStep || (isVisited && hasValue)
    : isVisited && hasValue;
  const isFirstStepComplete =
    isFirstStepCompleteProp &&
    checkIfStepHasValue(steps[0], formik.values as FormikValues);
  const hasVisibleInputs = step.inputs.some(
    input => input.contentType !== 'invisible',
  );
  const allowClick =
    ((!isSequential && (!isFirstStepRequired || isFirstStepComplete)) ||
      stepIndex < currentStep ||
      isComplete) &&
    (!isActive || !hasVisibleInputs);

  const onNavigateToStep = () => {
    if (!allowClick) return;
    if (onNavigate) {
      onNavigate(formik.values as FormikValues);
    }
    setCurrentStep(stepIndex);
  };

  const StatusIcon = isError ? Cancel : CheckCircle;

  return (
    <Box
      boxSizing="border-box"
      p={2.5}
      onClick={onNavigateToStep}
      display="flex"
      alignItems="start"
      gap={2}
      sx={{
        cursor: allowClick ? 'pointer' : 'default',
      }}>
      <Stack height={ICON_HEIGHT} display="flex" justifyContent="center">
        <StatusIcon
          width={22}
          htmlColor={
            isError
              ? theme.palette.primary.main
              : isComplete
              ? theme.palette.secondary.main
              : theme.palette.darkerLightGrey.main
          }
        />
      </Stack>
      <Stack gap={2.5} boxSizing="border-box" width="100%" overflow="hidden">
        <Box display="flex" justifyContent="space-between" gap={1}>
          <Stack minHeight={ICON_HEIGHT} justifyContent="center">
            <Typography variant="h3">{title}</Typography>
            <Typography
              variant="body2"
              whiteSpace="pre-line"
              sx={{
                color: isComplete ? 'text.primary' : 'text.secondary',
              }}>
              {isVisited ? visitedDescription ?? description : description}
            </Typography>
          </Stack>
          {!isSequential &&
            (!isActive || !hasVisibleInputs) &&
            (!isFirstStepRequired || isFirstStepComplete) && (
              <Button
                variant="light"
                sx={{
                  height: 'fit-content',
                }}>
                <span>{selectButtonText || 'Edit'}</span>
              </Button>
            )}
        </Box>
        {isActive && hasVisibleInputs && (
          <Stack gap={inputsGap} boxSizing="border-box">
            {banner}
            {step.inputs.map(input => (
              <StepsFlowInput
                key={input.name.toString()}
                currentStep={currentStep}
                setCurrentStep={setCurrentStep}
                isSequential={isSequential}
                input={input}
                step={stepProp}
                steps={steps}
                submitText={submitText}
              />
            ))}
            <Box display="flex" alignItems="center" gap={1}>
              {isSequential && currentStep > 0 && (
                <Button
                  variant="light"
                  onClick={() => {
                    setCurrentStep(prevStep => prevStep - 1);
                  }}>
                  Back
                </Button>
              )}
              <LoadingButton
                variant="base"
                color="primary"
                loading={formik.isSubmitting && isSubmit}
                disabled={isDisabled}
                type={isSubmit ? 'submit' : 'button'}
                onClick={() => {
                  if (isSubmit) return;
                  setCurrentStep(prevStep =>
                    Math.min(prevStep + 1, steps.length - 1),
                  );
                }}
                sx={{
                  position: important('relative'),
                  top: important('0'),
                  left: important('0'),
                  right: important('0'),
                  bottom: important('0'),
                  zIndex: important('1'),
                  margin: important('0'),
                }}>
                <span>
                  {isSubmit
                    ? submitText || 'Submit'
                    : buttonText || (isSequential ? 'Continue' : 'Save')}
                </span>
              </LoadingButton>
            </Box>
          </Stack>
        )}
      </Stack>
    </Box>
  );
}

export default StepsFlowStep;
